import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Sparkles, PauseCircle, Play, MessageCircleReply, Inbox } from "lucide-react";
import { useSmartLeads } from "@/hooks/useSmartLeads";
import { SmartDraftDialog } from "@/components/smartleads/SmartDraftDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smart-leads`;

// Smart Follow-Up strip on the Lead detail page: cadence, reply state, AI draft & send.
export const LeadSmartPanel = ({ leadId, onChange }) => {
  const sl = useSmartLeads();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setS((await axios.get(`${API}/lead/${leadId}`)).data); } catch (e) { setS(null); }
  }, [leadId]);
  useEffect(() => { load(); }, [load, sl.outbox, sl.ai]);

  if (!s || !s.open) return null;
  const changed = () => { load(); onChange?.(); };
  const lead = { id: s.lead_id, name: s.name, pet: s.pet, phone: s.phone, gingr: s.has_gingr, paused: s.paused,
    reply_snippet: s.reply_snippet, why: s.why, nba: s.nba };

  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="lead-smart-panel">
      <Sparkles className="w-5 h-5 text-amber-500" />
      <div className="text-sm text-slate-700 min-w-0">
        <b>Smart Follow-Up</b> · <span data-testid="lead-smart-cadence">{s.cadence_day}</span> · {s.attempts}/{s.max_attempts} attempts
        {s.score != null && <> · AI {s.score}</>}
        {s.nba && <span className="text-cyan-700 font-medium"> · Next: {s.nba}</span>}
        {s.outbox_status && <span className="ml-2 inline-flex items-center text-[11px] font-semibold text-emerald-700" data-testid="lead-smart-outbox"><Inbox className="w-3 h-3 mr-0.5" />{s.outbox_status === "pending" ? "In outbox" : s.outbox_status}</span>}
      </div>
      {s.paused && (
        <div className="flex items-center gap-1.5 text-[12px] text-rose-700 font-semibold" data-testid="lead-smart-replied">
          <PauseCircle className="w-4 h-4" /> Replied · paused{s.reply_snippet ? ` — “${s.reply_snippet.slice(0, 60)}”` : ""}
        </div>
      )}
      <div className="ml-auto flex gap-2">
        {s.paused ? (
          <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={async () => { await sl.resume(s.lead_id); changed(); }} data-testid="lead-smart-resume-btn">
            <Play className="w-3.5 h-3.5 mr-1" />Resume
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={async () => { await sl.markReplied(s.lead_id); changed(); }} data-testid="lead-smart-mark-replied-btn">
            <MessageCircleReply className="w-3.5 h-3.5 mr-1" />Mark replied
          </Button>
        )}
        <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-600" onClick={() => setOpen(true)} data-testid="lead-smart-draft-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1" />AI draft &amp; send
        </Button>
      </div>
      <SmartDraftDialog ids={open ? [s.lead_id] : []} getLead={() => lead} sl={sl} onClose={() => setOpen(false)} onChanged={changed} />
    </div>
  );
};
